import React, { useRef, useEffect } from 'react'
import {
  Control,
  Controller,
  FieldErrors,
  useFieldArray,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form'
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd'
import { msgid, ngettext, t } from 'ttag'
import { ReactComponent as RemoveIcon } from '../../../../assets/v3/icons/remove.svg'
import DragHandleIcon from '@material-ui/icons/DragHandle'
import { Box, IconButton, TextField } from '@material-ui/core'
import { FormData } from '../../ChallengeAdmin'
import { activityTypes } from '../../activityTypes'
import ChallengePointsUnitField from './ChallengePointsUnitField'
import { CustomTypeCategory, customUnits } from '../../../../services/CustomTypes'
import ChallengePointsActivityTypeField from './ChallengePointsActivityTypeField'
import ChallengePointsAmountField from './ChallengePointsAmountField'
import { CustomType, ProgressType } from '../../../../services/api.types'
import ChallengePointsCustomUnitField from './ChallengePointsCustomUnitField'
import { isAutomatedType, isDistanceType, isSimpleDayType } from '../../../../common/helpers/challenges'
import ChallengePointsDailyMaxField from './ChallengePointsDailyMaxField'
import { darkBlue } from '../../../../styles/material-ui-theme-v2'
import { ReactComponent as InfoIcon } from '../../../../assets/v2/icons/icon-info-gray.svg'
import { dailyMaxLabel } from '../../../ChallengeInfo/ChallengeInfo'
import ChallengePointsTooltip from './ChallengePointsTooltip'
import { isRtl } from '../../../../i18nInit'
import ChallengePointsIconField from './ChallengePointsIconField'
import ChallengePointsTotalMaxField from './ChallengePointsTotalMaxField'
import ChallengePointsMoreFeatures from './ChallengePointsMoreFeatures'
import { UserCompanyDto } from '../../../../services/api.types'
import { hasCompanyFeatureFlag } from '../../../../common/helpers/featureFlags'
import { scrollToBottom } from '../../../../common/helpers/scroll'
import { ACTIVITY_GROUPS_CONFIG } from '../ChallengeActivitySelect/ChallengeActivitySelect'
import YumAddButton from '../../../../components/YumAddButton/YumAddButton'

interface ChallengePointsProps {
  control: Control<FormData, any>
  watch: UseFormWatch<FormData>
  register: UseFormRegister<FormData>
  setValue: UseFormSetValue<FormData>
  errors: FieldErrors<FormData>
  userCompanies: UserCompanyDto[]
  isEdit: boolean
}

export type PointsLogicId = string

export interface PointsLogicTemplate {
  category: CustomTypeCategory
  value: number
  unit: CustomType
  points: number
  customActivityName?: string
  customUnitName?: string
  simpleManualEntry?: boolean
  dailyGoal?: number
  totalMax?: number
  icon?: CustomTypeCategory
  dailyGoalEnabled?: boolean
  totalMaxEnabled?: boolean
  // description: string
}

export interface PointsLogic extends PointsLogicTemplate {
  id: PointsLogicId
  dayValue: number
  dayPoints: number
  dayData: any
}

export const ignoreCategoriesForPoints = [
  ProgressType.POINTS,
  ProgressType.CUSTOM,
  ProgressType.ALL,
  ProgressType.CALORIES,
  ProgressType.TOTAL_POINTS,
]

// Helper function to get ordered activities from ACTIVITY_GROUPS_CONFIG
const getOrderedActivities = () => {
  // Flatten the configuration to get ordered list of activities
  const orderedActivities: CustomTypeCategory[] = []
  ACTIVITY_GROUPS_CONFIG.forEach((group) => {
    if (Array.isArray(group.activities)) {
      orderedActivities.push(...group.activities)
    }
  })
  return orderedActivities
}

const getNextUnusedActivityType = (fields: any[]) => {
  // Get the ordered activities from ACTIVITY_GROUPS_CONFIG
  const orderedActivities = getOrderedActivities()

  // Find the first unused activity in the configured order
  const unusedCategory = orderedActivities.find(
    (activityCategory) =>
      !fields.some(
        (field) =>
          field.category === activityCategory ||
          // @ts-ignore
          ignoreCategoriesForPoints.includes(activityCategory),
      ),
  )

  return unusedCategory || CustomTypeCategory.CUSTOM_SIMPLE
}

const getFirstUnitForCategory = (category: CustomTypeCategory) => {
  if ([CustomTypeCategory.CUSTOM_SIMPLE, CustomTypeCategory.CUSTOM_WITH_AMOUNT].includes(category)) {
    return CustomType.CUSTOM
  }
  return customUnits.find((c) => c.category === category)?.id
}

export const checkPointsError = (errors: any, index: number, name: string) => {
  // @ts-ignore
  return errors.challengePoints?.[index]?.[name]
  //return !!errors.challengePoints?.[index]?.[name]
}

export default function ChallengePoints({
  control,
  setValue,
  watch,
  errors,
  userCompanies,
  isEdit,
}: ChallengePointsProps) {
  const { fields, append, move, remove, update } = useFieldArray({
    control,
    name: 'challengePoints',
  })
  const childrenRef = useRef<Record<string, (sourceIndex: number, destIndex: number) => void>>({})
  const containerRef = useRef<HTMLDivElement>(null)
  const shouldScrollToBottom = useRef(false)

  // Effect to scroll to bottom after adding a new activity
  useEffect(() => {
    if (shouldScrollToBottom.current && containerRef.current) {
      // Use setTimeout to ensure the DOM has updated with the new field
      setTimeout(() => {
        // Scroll to the very bottom of the page, accounting for any fixed elements
        // window.scrollTo({
        //   top: document.documentElement.scrollHeight,
        //   behavior: 'smooth',
        // })

        // Prevent focus on any input fields by blurring any focused element
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur()
        }

        scrollToBottom()
      }, 0) // Small delay to ensure DOM update

      shouldScrollToBottom.current = false
    }
  }, [fields.length])

  const reorder = (result: any) => {
    const { source, destination, type } = result
    if (!destination) {
      return
    }
    const sourceIndex = source.index
    const destIndex = destination.index

    if (type === 'parentContainer') {
      move(sourceIndex, destIndex)
    } else if (type === 'childContainer' && source.droppableId) {
      const reorderChild = childrenRef.current[source.droppableId]
      if (reorderChild) {
        reorderChild(sourceIndex, destIndex)
      }
    }
  }

  const handleAdd = () => {
    const category = getNextUnusedActivityType(fields)
    const unit = getFirstUnitForCategory(category)

    // Only auto-scroll to bottom on new challenge creation, not when editing
    if (!isEdit) {
      shouldScrollToBottom.current = true
    }

    append({
      category,
      value: 1,
      unit,
      points: 1,
      customActivityName: '',
      customUnitName: '',
      simpleManualEntry: false,
      dailyGoalEnabled: false,
      totalMaxEnabled: false,
    })
  }

  // Hack to re-render form. Refactor if you know any better way.
  const onActivityTypeChange = (index: number, event: any) => {
    move(0, 0)

    const category = event.target.value as CustomTypeCategory
    if (category === CustomTypeCategory.CUSTOM_SIMPLE || category === CustomTypeCategory.BONUS_SIMPLE) {
      //setValue(`challengePoints.${index}.value`, 1)
      setValue(`challengePoints.${index}.unit`, CustomType.CUSTOM)
    } else if (
      category === CustomTypeCategory.CUSTOM_WITH_AMOUNT ||
      category === CustomTypeCategory.BONUS_WITH_AMOUNT
    ) {
      setValue(`challengePoints.${index}.unit`, CustomType.CUSTOM)
    } else {
      setValue(`challengePoints.${index}.unit`, getFirstUnitForCategory(category))
    }

    if (category === CustomTypeCategory.CUSTOM_SIMPLE || category === CustomTypeCategory.CUSTOM_WITH_AMOUNT) {
      setValue(`challengePoints.${index}.icon`, CustomTypeCategory.CUSTOM_SIMPLE)
    } else if (category === CustomTypeCategory.BONUS_SIMPLE || category === CustomTypeCategory.BONUS_WITH_AMOUNT) {
      setValue(`challengePoints.${index}.icon`, CustomTypeCategory.BONUS_SIMPLE)
    } else {
      setValue(`challengePoints.${index}.icon`, undefined)
    }

    /*if (isSimpleDayType(category)) {
      setValue(`challengePoints.${index}.value`, 1)
    }*/

    // Reset values
    setValue(`challengePoints.${index}.value`, 1)
    setValue(`challengePoints.${index}.dailyGoal`, undefined)
    setValue(`challengePoints.${index}.simpleManualEntry`, false)
    // Don't reset visibility settings - preserve user's manual choices
  }

  const manualEntryEnabled = watch('manualEntryEnabled')

  const challengeCompanyId = watch('companyId')
  const unlimitedActivities = hasCompanyFeatureFlag(userCompanies, challengeCompanyId, 'unlimitedPointsActivities')
  const unlimitedPointsValue = hasCompanyFeatureFlag(userCompanies, challengeCompanyId, 'unlimitedPointsValue')
  const unlimitedPointsPerActivity = hasCompanyFeatureFlag(
    userCompanies,
    challengeCompanyId,
    'unlimitedPointsPerActivity',
  )

  return (
    <div ref={containerRef}>
      <DragDropContext onDragEnd={reorder}>
        <Droppable droppableId="parent" type="parentContainer">
          {(provided) => (
            <ul
              ref={provided.innerRef}
              {...provided.droppableProps}
              style={{ listStyle: 'none', padding: 0, margin: 0 }}
            >
              {fields.map((item, index) => {
                const units = customUnits.filter((unit, keyIndex) => unit.category === item.category)
                const isActivityIndex = item.category === ProgressType.ALL
                const isSimpleCustom =
                  item.category === CustomTypeCategory.CUSTOM_SIMPLE ||
                  item.category === CustomTypeCategory.BONUS_SIMPLE
                const isCustomWithAmount =
                  item.category === CustomTypeCategory.CUSTOM_WITH_AMOUNT ||
                  item.category === CustomTypeCategory.BONUS_WITH_AMOUNT
                const onlyOneUnit = units.length == 1 || isActivityIndex || isSimpleCustom
                const value = watch(`challengePoints.${index}.value`)
                const unit = onlyOneUnit
                  ? isActivityIndex
                    ? '%'
                    : isSimpleCustom || units[0].text === t`days`
                      ? ngettext(msgid`day`, `days`, Number(value) || 2)
                      : units[0].text
                  : undefined
                return (
                  <Draggable key={item.id} draggableId={item.id} index={index}>
                    {(provided, { isDragging }) => (
                      <li ref={provided.innerRef} {...provided.draggableProps}>
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'row',
                            gap: 10,
                          }}
                        >
                          {/*<div style={{ paddingTop: 15 }}>{index + 1}.</div>*/}
                          <div style={{ flexGrow: 1 }}>
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'row',
                                marginRight: isRtl ? 0 : fields.length == 1 ? 48 : 0,
                                marginLeft: !isRtl ? 0 : fields.length == 1 ? 48 : 0,
                              }}
                            >
                              {/* Icon start */}
                              {[
                                CustomTypeCategory.CUSTOM_SIMPLE,
                                CustomTypeCategory.CUSTOM_WITH_AMOUNT,
                                CustomTypeCategory.BONUS_SIMPLE,
                                CustomTypeCategory.BONUS_WITH_AMOUNT,
                              ].includes(item.category) ? (
                                <div
                                  style={{
                                    marginRight: isRtl ? 'unset' : '10px',
                                    marginLeft: !isRtl ? 'unset' : '10px',
                                  }}
                                >
                                  <ChallengePointsIconField
                                    name={`challengePoints.${index}.icon`}
                                    fields={fields}
                                    index={index}
                                    /*defaultIcon={
                                      [CustomTypeCategory.BONUS_SIMPLE, CustomTypeCategory.BONUS_WITH_AMOUNT].includes(
                                        item.category,
                                      )
                                        ? CustomTypeCategory.POINTS
                                        : CustomTypeCategory.CUSTOM
                                    }*/
                                    {...{ control }}
                                  />
                                </div>
                              ) : null}
                              {/* Icon end */}

                              <ChallengePointsActivityTypeField
                                name={`challengePoints.${index}.category`}
                                onActivityTypeChange={onActivityTypeChange}
                                fields={fields}
                                index={index}
                                {...{ control }}
                              />

                              {fields.length > 1 && (
                                <IconButton
                                  style={{
                                    flexShrink: 0,
                                    marginBottom: '10px',
                                    position: 'relative',
                                    right: isRtl ? 'unset' : '-10px',
                                    left: !isRtl ? 'unset' : '-10px',
                                  }}
                                  title={t`Remove`}
                                  aria-label={t`Remove`}
                                  onClick={() => remove(index)}
                                >
                                  <RemoveIcon
                                    style={{
                                      width: 24,
                                      height: 24,
                                    }}
                                  />
                                </IconButton>
                              )}
                            </div>

                            <div
                              style={{ marginRight: isRtl ? 'unset' : '48px', marginLeft: !isRtl ? 'unset' : '48px' }}
                            >
                              {[
                                CustomTypeCategory.CUSTOM_SIMPLE,
                                CustomTypeCategory.CUSTOM_WITH_AMOUNT,
                                CustomTypeCategory.BONUS_SIMPLE,
                                CustomTypeCategory.BONUS_WITH_AMOUNT,
                              ].includes(item.category) && (
                                <Controller
                                  name={`challengePoints.${index}.customActivityName`}
                                  rules={{
                                    required: true,
                                  }}
                                  control={control}
                                  render={({ field: { ref, ...otherField } }) => (
                                    <TextField
                                      style={{ marginTop: 0, width: '100%' }}
                                      label={t`Enter activity name`}
                                      margin="dense"
                                      error={checkPointsError(errors, index, 'customActivityName')}
                                      variant="filled"
                                      InputProps={{
                                        disableUnderline: true,
                                      }}
                                      inputProps={{
                                        // This disables the browser autocomplete.
                                        autoComplete: 'new-password',
                                        'aria-required': 'true',
                                      }}
                                      {...otherField}
                                    />
                                  )}
                                />
                              )}
                              {/*{![CustomTypeCategory.CUSTOM_SIMPLE, CustomTypeCategory.CUSTOM_WITH_AMOUNT].includes(
                                item.category,
                              ) && (
                                <Controller
                                  name={`challengePoints.${index}.description`}
                                  control={control}
                                  render={({ field: { ref, ...otherField } }) => (
                                    <TextField
                                      style={{ marginTop: 0, width: '100%' }}
                                      label={t`Description`}
                                      margin="dense"
                                      variant="filled"
                                      InputProps={{
                                        disableUnderline: true,
                                      }}
                                      {...otherField}
                                    />
                                  )}
                                />
                              )}*/}
                            </div>

                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'row',
                                gap: 10,
                                marginRight: '-10px',
                                //marginRight: isRtl ? 'unset' : '48px',
                                //marginLeft: !isRtl ? 'unset' : '48px',
                              }}
                            >
                              <div style={{ flex: 1 }}>
                                <ChallengePointsAmountField
                                  name={`challengePoints.${index}.value`}
                                  label={t`Value`}
                                  max={unlimitedPointsValue ? undefined : 100000}
                                  unit={unit}
                                  disabled={
                                    item.category === CustomTypeCategory.CUSTOM_SIMPLE ||
                                    item.category === CustomTypeCategory.BONUS_SIMPLE ||
                                    isSimpleDayType(item.category)
                                  }
                                  error={checkPointsError(errors, index, 'value')}
                                  isDistance={isDistanceType(item.unit)}
                                  {...{ control }}
                                />
                              </div>
                              {item.category === CustomTypeCategory.CUSTOM_WITH_AMOUNT ||
                              item.category === CustomTypeCategory.BONUS_WITH_AMOUNT ? (
                                <div style={{ flex: 1 }}>
                                  <ChallengePointsCustomUnitField
                                    name={`challengePoints.${index}.customUnitName`}
                                    category={item.category}
                                    error={checkPointsError(errors, index, 'customUnitName')}
                                    {...{ control }}
                                  />
                                </div>
                              ) : null}
                              {!onlyOneUnit && !isCustomWithAmount ? (
                                <div style={{ flex: 1 }}>
                                  <ChallengePointsUnitField
                                    name={`challengePoints.${index}.unit`}
                                    category={item.category}
                                    {...{ control }}
                                  />
                                </div>
                              ) : null}
                              <div
                                className="DragHandle"
                                style={{ display: 'flex', alignItems: 'center', marginBottom: '10px', height: '50px' }}
                                {...provided.dragHandleProps}
                              >
                                <DragHandleIcon />
                              </div>
                              {/*minWidth: '75px', maxWidth: '100px',*/}
                              <div style={{ flex: 1 }}>
                                <ChallengePointsAmountField
                                  name={`challengePoints.${index}.points`}
                                  label={t`Points`}
                                  max={unlimitedPointsPerActivity ? undefined : 100}
                                  error={checkPointsError(errors, index, 'points')}
                                  {...{ control }}
                                />
                              </div>

                              <ChallengePointsMoreFeatures
                                name={`challengePoints.${index}.simpleManualEntry`}
                                dailyGoalName={`challengePoints.${index}.dailyGoal`}
                                dailyGoalEnabledName={`challengePoints.${index}.dailyGoalEnabled`}
                                totalMaxEnabledName={`challengePoints.${index}.totalMaxEnabled`}
                                item={item}
                                index={index}
                                setValue={setValue}
                                watch={watch}
                                userCompanies={userCompanies}
                              />
                            </div>

                            <div style={{ display: 'flex', gap: '10px' }}>
                              {![
                                CustomTypeCategory.CUSTOM_SIMPLE,
                                CustomTypeCategory.BONUS_SIMPLE,
                                CustomTypeCategory.BONUS_WITH_AMOUNT,
                              ].includes(item.category)
                                ? !isSimpleDayType(item.category) &&
                                  watch(`challengePoints.${index}.dailyGoalEnabled`) && (
                                    <>
                                      <div style={{ flex: 1 }}>
                                        <ChallengePointsDailyMaxField
                                          name={`challengePoints.${index}.simpleManualEntry`}
                                          dailyGoalFieldName={`challengePoints.${index}.dailyGoal`}
                                          unitFieldName={`challengePoints.${index}.unit`}
                                          control={control}
                                          watch={watch}
                                          dailyGoalError={checkPointsError(errors, index, 'dailyGoal')}
                                          isCustom={
                                            item.category === CustomTypeCategory.CUSTOM_WITH_AMOUNT ||
                                            item.category === CustomTypeCategory.BONUS_WITH_AMOUNT
                                          }
                                          index={index}
                                        />
                                      </div>
                                      <div
                                        style={{
                                          marginRight: isRtl ? 'unset' : '22px',
                                          marginLeft: !isRtl ? 'unset' : '22px',
                                          display: 'flex',
                                          alignItems: 'center',
                                          paddingBottom: '10px',
                                        }}
                                      >
                                        <ChallengePointsTooltip
                                          title={`${dailyMaxLabel} = ${t`Threshold where surpassing does not increase points.`}${
                                            manualEntryEnabled || !isAutomatedType(item.unit)
                                              ? ' ' +
                                                t`If one-click manual entry is enabled, users can easily mark the daily max as done.`
                                              : ''
                                          }`}
                                          placement="top"
                                          enterTouchDelay={50}
                                          leaveTouchDelay={3000}
                                          arrow
                                        >
                                          <InfoIcon />
                                        </ChallengePointsTooltip>
                                      </div>
                                    </>
                                  )
                                : null}
                            </div>

                            {watch(`challengePoints.${index}.totalMaxEnabled`) && (
                              <div
                                style={{ marginRight: isRtl ? 'unset' : '48px', marginLeft: !isRtl ? 'unset' : '48px' }}
                              >
                                <ChallengePointsTotalMaxField
                                  name={`challengePoints.${index}.totalMax`}
                                  unitFieldName={`challengePoints.${index}.unit`}
                                  control={control}
                                  watch={watch}
                                  dailyGoalError={checkPointsError(errors, index, 'totalMax')}
                                  isCustom={
                                    item.category === CustomTypeCategory.CUSTOM_WITH_AMOUNT ||
                                    item.category === CustomTypeCategory.BONUS_WITH_AMOUNT
                                  }
                                  isSimple={
                                    item.category === CustomTypeCategory.CUSTOM_SIMPLE ||
                                    item.category === CustomTypeCategory.BONUS_SIMPLE
                                  }
                                  index={index}
                                />
                              </div>
                            )}
                          </div>
                        </div>

                        <div
                          style={{
                            padding: '6px 0 16px',
                          }}
                        >
                          <div
                            style={{
                              height: '1px',
                              backgroundColor: darkBlue,
                              opacity: isDragging || fields.length - 1 === index ? 0 : 0.25,
                            }}
                          ></div>
                        </div>
                      </li>
                    )}
                  </Draggable>
                )
              })}
              {provided.placeholder}
            </ul>
          )}
        </Droppable>

        {(unlimitedActivities || fields.length < 20) && (
          <Box display="flex" justifyContent="center" style={{ marginBottom: '20px' }}>
            <YumAddButton label={t`Add activity`} onClick={handleAdd} />
          </Box>
        )}
      </DragDropContext>
    </div>
  )
}
